import { faArrowDown, faArrowUp, faTrash } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { MinQuantityRule, NEW_MIN_QUANTITY_RULE, parseMinQuantityRules } from './minQuantityRules.ts';
import Button from '../../core/components/Button.tsx';
import BooleanField from '../../core/components/Fields/BooleanField.tsx';
import SelectField from '../../core/components/Fields/SelectField.tsx';
import StringField from '../../core/components/Fields/StringField.tsx';
import FormField from '../../core/components/FormField.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import { useToaster } from '../../core/components/Toast.tsx';

const ArrowUpIcon = withIcon(faArrowUp);
const ArrowDownIcon = withIcon(faArrowDown);
const DeleteIcon = withIcon(faTrash);

const PREFIX = 'configPage.document.minQuantityRules';

interface MinQuantityRuleCardProps {
    rule: MinQuantityRule;
    onRuleChange: (changes: Partial<MinQuantityRule>) => void;
    onBlur?: () => void;
    showErrors: boolean;
}

const MinQuantityRuleCard = ({ rule, onRuleChange, onBlur, showErrors }: MinQuantityRuleCardProps) => {
    const { t } = useTranslation('config');

    const modeOptions = [
        { label: t(`${PREFIX}.modeOptions.minimum`), value: 'minimum' },
        { label: t(`${PREFIX}.modeOptions.multiple`), value: 'multiple' },
    ];
    const quantityFieldOptions = [
        { label: t(`${PREFIX}.quantityFieldOptions.quantity`), value: 'quantity' },
        { label: t(`${PREFIX}.quantityFieldOptions.converted_quantity`), value: 'converted_quantity' },
    ];
    const valueLocaleOptions = [
        { label: t(`${PREFIX}.valueLocaleOptions.en`), value: 'en' },
        { label: t(`${PREFIX}.valueLocaleOptions.de`), value: 'de' },
    ];

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-lg border border-solid border-line-weak p-4 min-w-0 flex-1">
            <FormField
                label={t(`${PREFIX}.extraDataKey`)}
                helpText={t(`${PREFIX}.extraDataKeyHelpText`)}
                required
                error={
                    showErrors && !(rule.extra_data_key || '').trim()
                        ? { message: t(`${PREFIX}.extraDataKeyRequired`) }
                        : undefined
                }
            >
                <StringField
                    className="font-mono"
                    value={rule.extra_data_key}
                    placeholder={t(`${PREFIX}.extraDataKeyPlaceholder`)}
                    onValueChange={(extra_data_key) => onRuleChange({ extra_data_key })}
                    onBlur={onBlur}
                />
            </FormField>

            <FormField label={t(`${PREFIX}.mode`)} helpText={t(`${PREFIX}.modeHelpText`)}>
                <SelectField
                    options={modeOptions}
                    value={rule.mode}
                    required
                    onValueChange={(mode) => onRuleChange({ mode: mode as MinQuantityRule['mode'] })}
                />
            </FormField>

            <FormField label={t(`${PREFIX}.quantityField`)} helpText={t(`${PREFIX}.quantityFieldHelpText`)}>
                <SelectField
                    options={quantityFieldOptions}
                    value={rule.quantity_field}
                    required
                    onValueChange={(quantity_field) =>
                        onRuleChange({ quantity_field: quantity_field as MinQuantityRule['quantity_field'] })
                    }
                />
            </FormField>

            <FormField label={t(`${PREFIX}.valueLocale`)} helpText={t(`${PREFIX}.valueLocaleHelpText`)}>
                <SelectField
                    options={valueLocaleOptions}
                    value={rule.value_locale}
                    required
                    onValueChange={(value_locale) =>
                        onRuleChange({ value_locale: value_locale as MinQuantityRule['value_locale'] })
                    }
                />
            </FormField>

            <FormField label={t(`${PREFIX}.isBlocking`)} helpText={t(`${PREFIX}.isBlockingHelpText`)}>
                <BooleanField
                    value={rule.is_blocking ? 'true' : ''}
                    onValueChange={(is_blocking) => onRuleChange({ is_blocking: !!is_blocking })}
                />
            </FormField>

            <FormField label={t(`${PREFIX}.lookupDefinitionId`)} helpText={t(`${PREFIX}.lookupDefinitionIdHelpText`)}>
                <StringField
                    className="font-mono"
                    value={rule.lookup_definition_id || ''}
                    onValueChange={(lookup_definition_id) => onRuleChange({ lookup_definition_id })}
                    onBlur={onBlur}
                />
            </FormField>
        </div>
    );
};

interface MinQuantityRulesFieldProps {
    value?: MinQuantityRule[] | null;
    onValueChange: (value: MinQuantityRule[]) => void;
    onBlur?: () => void;
    showErrors?: boolean;
}

// Mirrors the delivery date RuleList (core/components/Rules): ordered rules, row actions, copy/paste, add
const MinQuantityRulesField = ({ value, onValueChange, onBlur, showErrors = false }: MinQuantityRulesFieldProps) => {
    const { t } = useTranslation('config');
    const { t: tCore } = useTranslation('core');
    const { publishToast } = useToaster();
    const rules = value || [];

    const updateRule = (index: number, changes: Partial<MinQuantityRule>) =>
        onValueChange(rules.map((rule, i) => (i === index ? { ...rule, ...changes } : rule)));

    const moveRule = (index: number, offset: number) => {
        const newRules = [...rules];
        [newRules[index], newRules[index + offset]] = [newRules[index + offset], newRules[index]];
        onValueChange(newRules);
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(JSON.stringify(rules));
        publishToast({ status: 'success', description: tCore('components.rules.copySuccessToast') });
    };

    const handlePaste = async () => {
        try {
            const pastedRules = parseMinQuantityRules(await navigator.clipboard.readText());
            onValueChange([...rules, ...pastedRules]);
            publishToast({ status: 'success', description: tCore('components.rules.pasteSuccessToast') });
        } catch (e) {
            console.error(e);
            publishToast({ status: 'error', description: t(`${PREFIX}.pasteErrorToast`) });
        }
    };

    return (
        <div className="flex flex-col gap-4 w-full">
            {rules.map((rule, index) => (
                <div className="flex gap-2" key={index}>
                    <MinQuantityRuleCard
                        rule={rule}
                        onRuleChange={(changes) => updateRule(index, changes)}
                        onBlur={onBlur}
                        showErrors={showErrors}
                    />
                    <div className="flex flex-col justify-start gap-2">
                        <Button
                            type="button"
                            aria-label={t(`${PREFIX}.removeRule`)}
                            onClick={() => onValueChange(rules.filter((_, i) => i !== index))}
                        >
                            <DeleteIcon />
                        </Button>
                        <Button
                            type="button"
                            aria-label={t(`${PREFIX}.moveRuleUp`)}
                            disabled={index === 0}
                            onClick={() => moveRule(index, -1)}
                        >
                            <ArrowUpIcon />
                        </Button>
                        <Button
                            type="button"
                            aria-label={t(`${PREFIX}.moveRuleDown`)}
                            disabled={index === rules.length - 1}
                            onClick={() => moveRule(index, 1)}
                        >
                            <ArrowDownIcon />
                        </Button>
                    </div>
                </div>
            ))}

            <div className="flex items-center gap-2 text-content-medium text-sm">
                <Button type="button" variant="ghost" onClick={handleCopy} disabled={rules.length === 0}>
                    {tCore('components.rules.copyButton')}
                </Button>
                <span>-</span>
                <Button type="button" variant="ghost" onClick={handlePaste}>
                    {tCore('components.rules.pasteButton')}
                </Button>
            </div>

            <Button type="button" onClick={() => onValueChange([...rules, { ...NEW_MIN_QUANTITY_RULE }])}>
                {tCore('components.rules.addRuleButton')}
            </Button>

            <span className="text-content-medium text-xs">{t(`${PREFIX}.helpText`)}</span>
        </div>
    );
};

export default MinQuantityRulesField;
